/**
 * XSS Sanitization Utility
 * Uses DOMPurify for robust, spec-compliant HTML sanitization
 */
import DOMPurify from 'dompurify';

/**
 * Escape HTML special characters to prevent XSS
 * @param {string} str - String to sanitize
 * @returns {string} - Sanitized string with all HTML stripped
 */
export const escapeHtml = (str) => {
  if (typeof str !== 'string') return '';
  return DOMPurify.sanitize(str, { ALLOWED_TAGS: [] });
};

/**
 * Sanitize HTML content - allows only safe tags and attributes
 * @param {string} html - HTML string to sanitize
 * @returns {string} - Sanitized HTML
 */
export const sanitizeHtml = (html) => {
  if (!html || typeof html !== 'string') return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'span', 'mark', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'table', 'thead', 'tbody', 'tr', 'th', 'td'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
  });
};

/**
 * Sanitize text - strips ALL HTML tags, returns plain text
 * @param {string} text - Text to sanitize
 * @returns {string} - Plain text with no HTML
 */
export const sanitizeText = (text) => {
  if (!text) return '';
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
};

/**
 * Sanitize URL - only allow safe protocols
 * @param {string} url - URL to sanitize
 * @returns {string} - Sanitized URL or empty string if unsafe
 */
export const sanitizeUrl = (url) => {
  if (typeof url !== 'string') return '';

  const trimmedUrl = url.trim().toLowerCase();

  const safeProtocols = ['http://', 'https://', 'mailto:', 'tel:', '/'];

  const isSafe = safeProtocols.some(protocol =>
    trimmedUrl.startsWith(protocol) || !trimmedUrl.includes(':')
  );

  if (!isSafe) return '';

  return url;
};

/**
 * Create safe HTML for dangerouslySetInnerHTML
 * @param {string} html - HTML string to sanitize
 * @returns {object} - Object suitable for dangerouslySetInnerHTML
 */
export const createSafeHtml = (html) => {
  return { __html: sanitizeHtml(html) };
};

/**
 * Sanitize object properties recursively
 * @param {object} obj - Object to sanitize
 * @returns {object} - Sanitized object
 */
export const sanitizeObject = (obj) => {
  if (typeof obj !== 'object' || obj === null) {
    return typeof obj === 'string' ? escapeHtml(obj) : obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item));
  }

  const sanitized = {};
  for (const key of Object.keys(obj)) {
    sanitized[key] = sanitizeObject(obj[key]);
  }
  return sanitized;
};

export default {
  escapeHtml,
  sanitizeHtml,
  sanitizeText,
  sanitizeUrl,
  createSafeHtml,
  sanitizeObject
};
